import { ApolloClient, gql as contentfulGQL } from '@apollo/client'
import { Maybe } from '@graphql-tools/utils'
import { Durations } from '@/constants/durations'
import { ContentfulClientOptions, getContentfulClient } from '@/services/ApolloClient'
import { fetchTitle, formatNonTitleLikeVerticalImagePath } from '@/services/ContentCatalog'
import { GET_EPISODE_BY_GUID_QUERY } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import {
  ContentLink,
  GetGuildPricingTicketsOptionQuery,
  GetGuildSignupPageCollectionQuery,
  GetGuildSignupPagesCollectionQuery,
  GetMarketingOfferPageCollectionQuery,
} from '@/types/codegen-contentful'
import { GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { DeepNonNullable } from '@/utils/types'
import { cacheService } from '../CacheService'
import { getValidSale } from '../ContentfulSaleService/shared'
import { getNowStreamingProjects, NowStreamingContent } from '../NowStreamingService'
import { fetchAll } from '../service-util'
import { GuildProjectSlugs, GuildSignupEmphasis, VALID_GUILD_SIGNUP_EMPHASIS_VALUES } from './GuildSignupPageTypes'

export const getGuildSignupPagesCollectionQuery = contentfulGQL/* GraphQL */ `
  query getGuildSignupPagesCollection($limit: Int, $skip: Int) {
    guildSignupPageCollection(limit: $limit, skip: $skip) {
      items {
        slug
        sys {
          id
          publishedAt
        }
      }
    }
  }
`

const GUILD_SIGNUP_VIDEO_LAYOUT_FRAGMENT = contentfulGQL/* GraphQL */ `
  fragment GuildSignupVideoLayoutFragment on GuildSignupVideoLayout {
    __typename
    sys {
      id
    }
    heading
    paragraph {
      json
    }
    backgroundVideoUrl
    backgroundImageUrl
    heroVideoUrl
    heroVideoThumbnailImageUrl
    heroCta {
      description
      buttonText
      buttonHref
    }
  }
`

const GUILD_SIGNUP_SINGLE_SHOW_LAYOUT_FRAGMENT = contentfulGQL/* GraphQL */ `
  fragment GuildSignupSingleShowLayoutFragment on GuildSignupSingleShowLayout {
    __typename
    sys {
      id
    }
    project {
      logo
      name
    }
    heroImages
  }
`

const GUILD_SIGNUP_MULTI_SHOW_LAYOUT_FRAGMENT = contentfulGQL/* GraphQL */ `
  fragment GuildSignupMultiShowLayoutFragment on GuildSignupMultiShowLayout {
    __typename
    sys {
      id
    }
    primaryImage
    heading
    paragraph {
      json
    }
  }
`

const GUILD_SIGNUP_SIMPLE_LAYOUT_FRAGMENT = contentfulGQL/* GraphQL */ `
  fragment GuildSignupSimpleLayoutFragment on GuildSignupSimpleLayout {
    __typename
    sys {
      id
    }
    primaryImage
    heading
    paragraph {
      json
    }
  }
`

const GUILD_SIGNUP_SCROLLING_LAYOUT_FRAGMENT = contentfulGQL/* GraphQL */ `
  fragment GuildSignupScrollingLayoutFragment on GuildSignupScrollingLayout {
    __typename
    sys {
      id
    }
    heading
    paragraph {
      json
    }
    backgroundHeroImage
    heroCta {
      description
      buttonText
      buttonHref
    }
  }
`

export const GET_GUILD_SIGNUP_PAGE_COLLECTION_QUERY = contentfulGQL/* GraphQL */ `
  ${GUILD_SIGNUP_VIDEO_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SINGLE_SHOW_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_MULTI_SHOW_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SIMPLE_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SCROLLING_LAYOUT_FRAGMENT}
  query getGuildSignupPageCollection($slug: String!, $locale: String, $preview: Boolean!, $linkLimit: Int) {
    guildSignupPageCollection(where: { slug: $slug }, locale: $locale, preview: $preview, limit: 1) {
      items {
        sys {
          id
        }
        slug
        redirectTo
        emphasis
        guildMemberHref
        guildMemberCta
        projectSlugs
        votingPhoneImageUrl
        layoutExperimentCollection {
          items {
            ... on GrowthbookExperimentMappings {
              sys {
                id
              }
              rolloutVariationId
            }
            ... on GuildSignupExplainerLayout {
              sys {
                id
              }
            }
            ... on GuildSignupSingleShowLayout {
              ...GuildSignupSingleShowLayoutFragment
            }
            ... on GuildSignupMultiShowLayout {
              ...GuildSignupMultiShowLayoutFragment
            }
            ... on GuildSignupSimpleLayout {
              ...GuildSignupSimpleLayoutFragment
            }
            ... on GuildSignupVideoLayout {
              ...GuildSignupVideoLayoutFragment
            }
            ... on GuildSignupScrollingLayout {
              ...GuildSignupScrollingLayoutFragment
            }
          }
        }
        layout {
          ... on GuildSignupSingleShowLayout {
            ...GuildSignupSingleShowLayoutFragment
          }
          ... on GuildSignupMultiShowLayout {
            ...GuildSignupMultiShowLayoutFragment
          }
          ... on GuildSignupSimpleLayout {
            ...GuildSignupSimpleLayoutFragment
          }
          ... on GuildSignupVideoLayout {
            ...GuildSignupVideoLayoutFragment
          }
          ... on GuildSignupScrollingLayout {
            ...GuildSignupScrollingLayoutFragment
          }
        }
        guildEarlyAccessSection {
          heading
          subHeading
          contentLinksCollection(limit: $linkLimit) {
            items {
              sys {
                id
              }
              title
              guid
              sortOrder
              region
              cloudinaryImagePath
              url
              contentType
            }
          }
          episodeListCollection(limit: $linkLimit) {
            items {
              sys {
                id
              }
              title
              description
              imageUrl
              releaseDate
            }
          }
        }
        explainerLayout {
          title
          description {
            json
          }
          backgroundImageUrl
          imagesCollection {
            items {
              altText
              item
            }
          }
          videoUrl
          disableSectionFade
          ctaColor
        }
        project {
          contentCatalogId
          logo
        }
        guildTestimonialSection {
          testimonialsCollection(where: { sort_exists: false }, limit: 5) {
            items {
              author
              company
              rating
              quote
            }
          }
          cinemaScore {
            rating
            author
            icon
          }
          tomatometer {
            rating
            author
            icon
          }
          audience {
            rating
            author
            icon
          }
          desktopBackgroundImageUrl
          tabletBackgroundImageUrl
          mobileBackgroundImageUrl
          guildSpecific
        }
        guildTicketsSection {
          title
          description {
            json
          }
          ticketsImageUrl
        }
        seo {
          name
          title
          description
          metaImage2
        }
      }
    }
  }
`

export const GET_MARKETING_OFFER_PAGE_COLLECTION_QUERY = contentfulGQL/* GraphQL */ `
  ${GUILD_SIGNUP_VIDEO_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SINGLE_SHOW_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_MULTI_SHOW_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SIMPLE_LAYOUT_FRAGMENT}
  ${GUILD_SIGNUP_SCROLLING_LAYOUT_FRAGMENT}
  query getMarketingOfferPageCollection($slug: String!, $locale: String, $preview: Boolean!) {
    marketingOfferSignupPageCollection(where: { slug: $slug }, locale: $locale, preview: $preview, limit: 1) {
      items {
        slug
        redirectTo
        emphasis
        guildMemberHref
        guildMemberCta
        projectSlugs
        sale
        layout {
          ... on GuildSignupSingleShowLayout {
            ...GuildSignupSingleShowLayoutFragment
          }
          ... on GuildSignupMultiShowLayout {
            ...GuildSignupMultiShowLayoutFragment
          }
          ... on GuildSignupSimpleLayout {
            ...GuildSignupSimpleLayoutFragment
          }
          ... on GuildSignupVideoLayout {
            ...GuildSignupVideoLayoutFragment
          }
          ... on GuildSignupScrollingLayout {
            ...GuildSignupScrollingLayoutFragment
          }
        }
        explainerLayout {
          title
          description {
            json
          }
          backgroundImageUrl
          imagesCollection {
            items {
              altText
              item
            }
          }
          videoUrl
          disableSectionFade
          ctaColor
        }
      }
    }
  }
`

export const GET_GUILD_PRICING_TICKETS_OPTION = contentfulGQL/* GraphQL */ `
  query getGuildPricingTicketsOption($locale: String, $preview: Boolean!) {
    guildPricingTicketsOptionCollection(locale: $locale, preview: $preview, limit: 1) {
      items {
        sys {
          id
        }
        title
        offerText {
          json
        }
        ticketsImageUrl
      }
    }
  }
`

export interface GetGuildPricingTicketsOptionArgs {
  opts: ContentfulClientOptions
  contentfulClient: ApolloClient<object>
}

export interface GetGuildSignupPageBySlugArgs {
  slug: string
  opts: { linkLimit?: number } & ContentfulClientOptions
  contentfulClient: ApolloClient<object>
}

type GuildSignupPageDataFromQuery = NonNullable<
  DeepNonNullable<GetGuildSignupPageCollectionQuery, 3>['guildSignupPageCollection']['items'][number]
>

export type GuildSignupPageData = Omit<GuildSignupPageDataFromQuery, 'projectSlugs' | 'emphasis'> & {
  emphasis: GuildSignupEmphasis
  projectSlugs?: GuildProjectSlugs
}

export type GuildPricingTicketsOption = NonNullable<
  DeepNonNullable<GetGuildPricingTicketsOptionQuery, 3>['guildPricingTicketsOptionCollection']['items'][number]
>

export type GuildSignupPage = {
  slug: string
}

export const getGuildSignupPageBySlug = async ({
  slug,
  opts,
  contentfulClient,
}: GetGuildSignupPageBySlugArgs): Promise<GuildSignupPageData | null> => {
  const { data } = await contentfulClient.query<GetGuildSignupPageCollectionQuery>({
    query: GET_GUILD_SIGNUP_PAGE_COLLECTION_QUERY,
    variables: {
      preview: opts.preview,
      slug,
      linkLimit: opts.linkLimit ?? 16,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  const guildSignupPage = data?.guildSignupPageCollection?.items?.[0] as Maybe<GuildSignupPageData>
  return guildSignupPage ? validateGuildSignupPage(guildSignupPage) : null
}

interface GetMarketingOfferPageBySlugArgs {
  slug: string
  opts: { linkLimit?: number } & ContentfulClientOptions
}

type GuildMarketingOfferPageDataFromQuery = NonNullable<
  DeepNonNullable<GetMarketingOfferPageCollectionQuery, 3>['marketingOfferSignupPageCollection']['items'][number]
>

export type GuildMarketingOfferPageData = Omit<GuildMarketingOfferPageDataFromQuery, 'projectSlugs' | 'emphasis'> & {
  emphasis: GuildSignupEmphasis
  projectSlugs?: GuildProjectSlugs
}

export type GuildMarketingOfferPage = {
  slug: string
}

export const getMarketingOfferPageBySlug = async ({
  slug,
  opts,
}: GetMarketingOfferPageBySlugArgs): Promise<GuildMarketingOfferPageData | null> => {
  const contentfulClient = getContentfulClient({
    preview: opts.preview,
    locale: opts.locale,
  })

  const { data } = await contentfulClient.query<GetMarketingOfferPageCollectionQuery>({
    query: GET_MARKETING_OFFER_PAGE_COLLECTION_QUERY,
    variables: {
      preview: opts.preview,
      slug,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  const marketingOfferPage = data?.marketingOfferSignupPageCollection?.items?.[0] as Maybe<GuildMarketingOfferPageData>
  return marketingOfferPage ? validateMarketingOfferPage(marketingOfferPage) : null
}

function validateGuildSignupPage(guildSignupPage: GuildSignupPageData): GuildSignupPageData {
  const maybeArray = guildSignupPage.projectSlugs?.projectSlugs
  if (
    !Array.isArray(maybeArray) ||
    (Array.isArray(maybeArray) && maybeArray.length > 0 && typeof maybeArray[0] !== 'string')
  ) {
    logger().error('The projectSlugs field on a guild signup page object is either missing or invalid.', {
      guildSignupPage,
    })
  }
  if (!VALID_GUILD_SIGNUP_EMPHASIS_VALUES.some((valid) => valid === guildSignupPage.emphasis)) {
    logger().error('The emphasis field on a guild signup page object is either missing or invalid.', {
      guildSignupPage,
    })
  }
  return guildSignupPage
}

function validateMarketingOfferPage(guildMarketingOfferPage: GuildMarketingOfferPageData): GuildMarketingOfferPageData {
  const maybeArray = guildMarketingOfferPage.projectSlugs?.projectSlugs
  if (
    !Array.isArray(maybeArray) ||
    (Array.isArray(maybeArray) && maybeArray.length > 0 && typeof maybeArray[0] !== 'string')
  ) {
    logger().error('The projectSlugs field on a guild marketing offer page object is either missing or invalid.', {
      guildMarketingOfferPage,
    })
  }
  const sale = getValidSale(guildMarketingOfferPage?.sale)
  if (!sale || typeof sale !== 'object') {
    logger().error('The sale field on a guild marketing offer page object is either missing or invalid.', {
      guildMarketingOfferPage,
    })
  }
  if (!VALID_GUILD_SIGNUP_EMPHASIS_VALUES.some((valid) => valid === guildMarketingOfferPage.emphasis)) {
    logger().error('The emphasis field on a guild marketing offer page object is either missing or invalid.', {
      guildMarketingOfferPage,
    })
  }
  return guildMarketingOfferPage
}

function validateGuildPricingTicketsOption(
  guildPricingTicketsOption: GuildPricingTicketsOption,
): GuildPricingTicketsOption {
  if (!guildPricingTicketsOption.title) {
    logger().error('The title field on a guild pricing tickets option object is either missing or invalid.', {
      guildPricingTicketsOption,
    })
  }
  return guildPricingTicketsOption
}

export type GetAllGuildSignupPagesResult = DeepNonNullable<
  GetGuildSignupPagesCollectionQuery,
  3
>['guildSignupPageCollection']['items']

export const getAllGuildSignupPages = async (): Promise<GetAllGuildSignupPagesResult> => {
  try {
    const key = cacheService.genCacheKey('guildSignupPageService', 'getAllGuildSignupPages')
    const cached = await cacheService.getParsed<GetAllGuildSignupPagesResult>(key)
    if (cached) {
      return cached
    }

    const increment = 1000
    const contentfulClient = getContentfulClient()
    const allProjects = await fetchAll({ increment }, async (skip) => {
      const { data } = await contentfulClient.query<GetGuildSignupPagesCollectionQuery>({
        query: getGuildSignupPagesCollectionQuery,
        variables: { limit: increment, skip },
        errorPolicy: 'all',
      })
      return data?.guildSignupPageCollection?.items ?? []
    })

    await cacheService.set(key, JSON.stringify(allProjects), { expireInSeconds: Durations.ONE_DAY_IN_SECONDS })
    return allProjects
  } catch (err) {
    logger().error('Error fetching all guild signup pages', err)
    return []
  }
}

export async function getLinkData(
  link: Maybe<ContentLink>,
  client: ApolloClient<object>,
  locale: string,
): Promise<LinkViewModel | null> {
  if (!link) return null

  if (!link.contentType) {
    return buildBaseLinkModel(link)
  } else if (link.contentType === 'episode') {
    return getEpisodeLinkData(link, client)
  } else if (link.contentType === 'catalog-title') {
    return getCatalogTitleLinkData(link, locale)
  }

  logger().warn(`Unsupported content type for link: ${link.contentType}`, { link })
  return null
}

function buildBaseLinkModel(link: ContentLink): LinkViewModel {
  return {
    id: link.sys.id,
    linkType: 'content-link',
    name: link.title ?? '',
    linkUrl: link.url ?? '',
  }
}

async function getEpisodeLinkData(link: ContentLink, client: ApolloClient<object>): Promise<LinkViewModel | null> {
  if (!link?.guid) return null

  const response = await client.query<GetEpisodeByGuidQuery>({
    query: GET_EPISODE_BY_GUID_QUERY,
    variables: { guid: link.guid },
    errorPolicy: 'all',
  })

  if (!response.data?.episode) return null

  const episode = response.data.episode

  return {
    imageUrl: link.cloudinaryImagePath ?? episode.posterCloudinaryPath,
    metadata: {
      episode: response.data.episode,
    },
    ...buildBaseLinkModel(link),
    linkType: 'episode',
  }
}

async function getCatalogTitleLinkData(link: ContentLink, locale: string): Promise<LinkViewModel | null> {
  const catalogTitle = await fetchTitle(locale, link.guid)

  const titleImageUrl = catalogTitle ? formatNonTitleLikeVerticalImagePath(catalogTitle) : null

  return {
    imageUrl: link.cloudinaryImagePath ?? titleImageUrl ?? undefined,
    metadata: {
      catalogTitle,
    },
    ...buildBaseLinkModel(link),
    linkType: 'catalog-title',
  }
}

export const fetchNowStreamingProjects = async (
  client: ApolloClient<object>,
  featuredSlugs: string[] | undefined,
): Promise<NowStreamingContent[]> => {
  try {
    const response = await getNowStreamingProjects(client)
    const definedProjects = response?.filter(isDefined).sort(sortNowStreaming(featuredSlugs)) ?? []

    return definedProjects
  } catch (e) {
    // Do nothing. If an error occurs, our apollo client will log it and we will just not show this rail
    return []
  }
}

const sortNowStreaming = (featuredSlugs?: string[]) => (a: NowStreamingContent, b: NowStreamingContent) => {
  if (featuredSlugs && featuredSlugs.length > 0) {
    if (featuredSlugs?.indexOf(a.project.slug as string) > featuredSlugs?.indexOf(b.project.slug as string)) {
      return -1
    } else if (featuredSlugs?.indexOf(a.project.slug as string) < featuredSlugs?.indexOf(b.project.slug as string)) {
      return 1
    }
  }
  return a?.project?.releaseDate > b?.project?.releaseDate ? -1 : 1
}

export function getExplainerLayout(pageData: GuildSignupPageData) {
  return pageData.explainerLayout ?? null
}

export function getEarlyAccessSection(pageData: GuildSignupPageData) {
  return pageData.guildEarlyAccessSection ?? null
}

export function getTestimonialSection(pageData: GuildSignupPageData) {
  return pageData.guildTestimonialSection ?? null
}

export function getTicketsSection(pageData: GuildSignupPageData) {
  return pageData.guildTicketsSection ?? null
}

export const getGuildPricingTicketsOption = async ({
  opts,
  contentfulClient,
}: GetGuildPricingTicketsOptionArgs): Promise<GuildPricingTicketsOption | null> => {
  const { data } = await contentfulClient.query<GetGuildPricingTicketsOptionQuery>({
    query: GET_GUILD_PRICING_TICKETS_OPTION,
    variables: {
      preview: opts.preview,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  const guildPricingTicketsOption = data?.guildPricingTicketsOptionCollection
    ?.items?.[0] as Maybe<GuildPricingTicketsOption>
  return guildPricingTicketsOption ? validateGuildPricingTicketsOption(guildPricingTicketsOption) : null
}
